import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalBody,
  Row,
  Col,
  Input,
  Button,
  Form,
  Table
} from 'reactstrap';

import ReactButton from 'components/Buttons/ReactButton';
import { useForm, Controller } from 'react-hook-form';
import { SpaceItens, Hr } from 'views/ProtocolConfiguration/styled';
import { ErrorMessage } from '@hookform/error-message';
import FlexSelect from 'components/Inputs/FlexSelect';
import { EntityStageRulesDefinition } from './EntityStageRulesDefinition';
import ProcessStageSettingService from './service';
import { toast } from 'react-toastify';
import { uniq } from 'lodash';

export const EntityStageConfigModal = ({
  isModalOpen,
  toggleModal,
  entityInfo,
  customFields,
  callBack = () => null,
  processId,
  entity
}) => {
  const hasInfo = !!entityInfo?.StageSettings;
  const { control, errors, setValue, handleSubmit, reset, watch, getValues } =
    useForm();
  const displayableFieldsIds = watch('displayableFields') ?? [];
  const blockedList = watch('blockedFields') ?? [];
  const requiredList = watch('requiredFields') ?? [];
  const [currentRule, setCurrentRule] = useState(null);
  const [isLoadingCustomFields, setIsLoadingCustomFields] = useState(false);
  const [isModalRuleOpen, setIsModalRuleOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [forceRender, setForceRender] = useState(false);

  useEffect(() => {
    if (forceRender) {
      setForceRender(false);
    }
  }, [forceRender]);

  const displayableFields = useMemo(() => {
    const fields = customFields.filter((field) =>
      displayableFieldsIds.includes(field.id)
    );
    return fields;
  }, [displayableFieldsIds, customFields]);

  useEffect(() => {
    let blockedFields = getValues('blockedFields') ?? [];
    let requiredFields = getValues('requiredFields') ?? [];

    const filteredRequiredFields = requiredFields.filter((field) =>
      displayableFieldsIds.includes(field)
    );

    const filteredBlockedFields = blockedFields.filter((field) =>
      displayableFieldsIds.includes(field)
    );
    setValue('requiredFields', filteredRequiredFields);
    setValue('blockedFields', filteredBlockedFields);
  }, [displayableFieldsIds]);

  const nonRequiredFields = useMemo(() => {
    const fields = displayableFields.filter((field) => {
      return !requiredList.includes(field.id);
    });
    return fields;
  }, [displayableFields, requiredList]);

  const fieldsInRules = useMemo(() => {
    const fieldList = rules.map((rule) => rule.fields).flat();
    const ruleSet = uniq(fieldList);
    return ruleSet;
  }, [rules]);

  useEffect(() => {
    if (entityInfo && entityInfo.StageSettings) {
      reset(entityInfo.StageSettings);
      setRules([...entityInfo?.StageSettings?.rules]);
    }
  }, [entityInfo]);

  useEffect(() => {
    if (!isModalRuleOpen) {
      setCurrentRule(null);
    }
  }, [isModalRuleOpen]);

  const toggleRuleModal = () => {
    setIsModalRuleOpen(!isModalRuleOpen);
  };

  const closeModal = () => {
    toggleModal();
  };

  const handleDeleteRule = (ruleName) => {
    const newRuleList = rules.filter((rule) => rule.name !== ruleName);
    setRules(newRuleList);
  };

  const handleEditRule = (rule) => {
    setCurrentRule(rule);
    toggleRuleModal();
    // abrir modal de edição da regra
  };

  const onSubmit = async (data) => {
    try {
      const dataToSend = {
        ...data,
        rules
      };
      dataToSend.processId = +processId;
      dataToSend.entity = entity;
      dataToSend.stageId = entityInfo.id;
      if (hasInfo) {
        dataToSend.id = entityInfo?.StageSettings?.id;
        await ProcessStageSettingService.update(dataToSend);
      } else {
        await ProcessStageSettingService.create(dataToSend);
      }
      callBack();
      closeModal();
    } catch (err) {
      console.log('err: ', err);
      const msg = err.response?.data?.message;
      toast.error(msg || 'Erro ao atualizar', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  return (
    <>
      <Modal
        className="modal-dialog-centered"
        isOpen={isModalOpen}
        style={{ width: '50vw', maxWidth: ' 50rem' }}
      >
        <div className="modal-header">
          <h6
            className="modal-title"
            id="modal-title-default"
            style={{
              width: '100%'
            }}
          >
            Ajustar Campos e Regras de Pontuação - {entityInfo.name}
          </h6>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={closeModal}
          >
            <span aria-hidden={true}>x</span>
          </button>
        </div>
        <ModalBody>
          <Form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-12">
              <Col md="12" className="mb-2">
                <label className="form-control-label">Campos Exibidos</label>
                <Controller
                  render={(props) => (
                    <FlexSelect
                      dataOptions={customFields}
                      isMulti={true}
                      value={props.value}
                      valueController={(name, value) => {
                        const group = [...value, ...fieldsInRules];
                        const unique = uniq(group);
                        setValue(name, unique);
                      }}
                      fieldName="displayableFields"
                      labelName="label"
                      valueName="id"
                      isClearable={false}
                      disabled={isLoadingCustomFields}
                      noOptionsMessage={() => 'Nenhuma campo para ser exibida'}
                    />
                  )}
                  control={control}
                  name="displayableFields"
                  defaultValue=""
                />
                <ErrorMessage
                  errors={errors}
                  name="displayableFields"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
              <Col md="12" className="mb-2">
                <label className="form-control-label">Campos Bloqueados</label>
                <Controller
                  render={(props) => (
                    <FlexSelect
                      dataOptions={customFields}
                      isMulti={true}
                      value={props.value}
                      valueController={setValue}
                      fieldName="blockedFields"
                      labelName="label"
                      valueName="id"
                      isClearable={true}
                      disabled={isLoadingCustomFields}
                      noOptionsMessage={() => 'Nenhuma fase para ser exibida'}
                    />
                  )}
                  control={control}
                  name="blockedFields"
                  defaultValue={[]}
                />
                <ErrorMessage
                  errors={errors}
                  name="blockedFields"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
              <Col md="12" className="mb-3">
                <label className="form-control-label">Campos Requeridos</label>
                <Controller
                  render={(props) => (
                    <FlexSelect
                      dataOptions={customFields}
                      isMulti={true}
                      value={props.value}
                      valueController={setValue}
                      fieldName="requiredFields"
                      labelName="label"
                      valueName="id"
                      isClearable={true}
                      noOptionsMessage={() => 'Nenhuma fase para ser exibida'}
                    />
                  )}
                  control={control}
                  name="requiredFields"
                  defaultValue={[]}
                />
                <ErrorMessage
                  errors={errors}
                  name="requiredFields"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
              <Col md="12" className="mb-2">
                <Table
                  bordered={true}
                  striped={true}
                  size="sm"
                  className="text-center"
                >
                  <thead>
                    <tr>
                      <th>Regra da Pontuação</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules &&
                      rules.map((rule, index) => (
                        <tr key={rule.name}>
                          <td className="text-left">{rule.name}</td>
                          <td>
                            <Button
                              size="sm"
                              color="info"
                              onClick={() => {
                                handleEditRule({ ...rule, index });
                              }}
                            >
                              <li className="fas fa-user-edit"></li>
                            </Button>
                            <Button
                              size="sm"
                              color="danger"
                              onClick={() => {
                                handleDeleteRule(rule.name);
                              }}
                            >
                              <li className="fas fa-trash"></li>
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </Col>
              <Col md="12" className="mb-2">
                <div
                  className="addRule"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                    marginTop: '0.5rem'
                  }}
                >
                  <p>Adicionar nova regra de pontuação</p>
                  <Button
                    size="sm"
                    color="info"
                    onClick={() => {
                      toggleRuleModal();
                    }}
                  >
                    <li className="fas fa-plus"></li>
                  </Button>
                </div>
              </Col>
              <Col sm="12" className="mb-2">
                <label className="form-control-label">Prioridade</label>
                <Controller
                  as={
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Prioridade"
                    />
                  }
                  control={control}
                  name="priority"
                  defaultValue=""
                />
                <ErrorMessage
                  errors={errors}
                  name="priority"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>

              <Col sm="12" className="mb-2">
                <div style={{ marginLeft: '20px' }}>
                  <Controller
                    render={(props) => (
                      <>
                        <Input
                          type="checkbox"
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                        <label className="form-control-label">
                          Atualizar fase de forma automática
                        </label>
                      </>
                    )}
                    control={control}
                    name="automaticChange"
                    defaultValue=""
                  />
                  <ErrorMessage
                    errors={errors}
                    name="automaticChange"
                    render={({ message }) => errorFormMessage(message)}
                  />
                </div>
              </Col>
            </Row>
          </Form>
          <SpaceItens />
          <Hr />
          <>
            <ReactButton
              btnColor="confirmation"
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Salvar
            </ReactButton>
            <ReactButton btnColor="cancelation" onClick={closeModal}>
              Cancelar
            </ReactButton>
          </>
        </ModalBody>
      </Modal>
      {isModalRuleOpen && (
        <EntityStageRulesDefinition
          isModalRuleOpen={isModalRuleOpen}
          toggleRuleModal={toggleRuleModal}
          setRuleList={setRules}
          ruleToEdit={currentRule}
          fields={customFields}
        />
      )}
    </>
  );
};
